import React, { useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { useFormik, FormikProvider } from "formik";
import * as Yup from "yup";
import CustomTextArea from "../../../common/CustomTextArea";
import "./EditSchool.css";

const ChangeSchoolStatusModal = ({
  showModal,
  handleClose,
  schoolName,
  action, // 'delete' or 'restore'
  loading,
  error,
  onSubmit,
  requireReason = true, // true = show reason textarea, false = simple confirmation
}) => {
  const [isSuccess, setIsSuccess] = useState(false);

  const isDisable = action === "delete";

  const validationSchema = requireReason
    ? Yup.object({
        reason: Yup.string().trim().required("Please provide a reason."),
      })
    : Yup.object({});

  const formik = useFormik({
    initialValues: {
      reason: "",
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      const success = await onSubmit(action, values.reason || "");
      if (success) {
        setIsSuccess(true);
        resetForm();
      }
    },
  });

  const handleModalClose = () => {
    formik.resetForm();
    setIsSuccess(false);
    handleClose();
  };

  const handleSuccessContinue = () => {
    handleModalClose();
  };

  const getIcon = () => {
    if (isSuccess) {
      return (
        <div
          className="d-inline-flex align-items-center justify-content-center rounded-circle bg-success bg-opacity-10"
          style={{ width: "64px", height: "64px" }}
        >
          <i className="bi bi-check-circle-fill text-success fs-2"></i>
        </div>
      );
    }
    if (error) {
      return (
        <div
          className="d-inline-flex align-items-center justify-content-center rounded-circle bg-danger bg-opacity-10"
          style={{ width: "64px", height: "64px" }}
        >
          <i className="bi bi-x-circle-fill text-danger fs-2"></i>
        </div>
      );
    }
    return null;
  };

  const getTitle = () => {
    if (isSuccess) return isDisable ? "School Disabled Successfully" : "School Enabled Successfully";
    if (error) return isDisable ? "Disable Failed" : "Enable Failed";
    return isDisable ? "Disable School" : "Enable School";
  };

  return (
    <Modal
      show={showModal}
      onHide={handleModalClose}
      centered
      className="custom-modal"
      backdrop={loading ? "static" : true}
      keyboard={!loading}
    >
      <Modal.Body className="p-4 m-2">
        {/* Icon */}
        <div className="text-center mb-3">
          {getIcon()}
        </div>

        {/* Title */}
        <div className="text-center mb-3">
          <h5 className={`text-${isSuccess ? "success" : (error || isDisable) ? "danger" : "success"} fw-bold fs-3`}>
            {getTitle()}
          </h5>
        </div>

        {/* Success View */}
        {isSuccess && (
          <>
            <p className="text-center text-success mb-4">
              {`The school has been ${isDisable ? 'disabled' : 'enabled'} successfully.`}
            </p>
            <div className="d-flex justify-content-center">
              <Button className="custom-primary-button" onClick={handleSuccessContinue}>
                Continue
              </Button>
            </div>
          </>
        )}

        {/* Error View */}
        {error && !isSuccess && (
          <>
            <p className="text-center text-danger mb-4">{error}</p>
            <div className="d-flex flex-column flex-md-row justify-content-center gap-3">
              <button
                type="button"
                className="custom-cancel-button"
                onClick={handleModalClose}
                disabled={loading}
              >
                Cancel
              </button>
              <Button
                type="button"
                className="custom-delete-button"
                onClick={() => {
                   formik.handleSubmit();
                }}
                disabled={loading}
              >
                {loading ? "Retrying..." : "Retry"}
              </Button>
            </div>
          </>
        )}

        {/* Input Form View */}
        {!isSuccess && !error && (
          <>
            <p className="text-center mb-4 text-muted">
              School: <strong>{schoolName}</strong>
            </p>

            <div className="text-start">
              {isDisable ? (
                <div className="alert alert-warning" style={{ fontSize: "14px", padding: "10px 15px" }}>
                  <strong>Warning:</strong> This will disable the school and all the teachers, students, and other admins inside this school.
                </div>
              ) : (
                <div className="alert alert-info" style={{ fontSize: "14px", padding: "10px 15px" }}>
                  <strong>Info:</strong> This will enable the school.
                </div>
              )}
            </div>

            {requireReason && (
              <FormikProvider value={formik}>
                <form onSubmit={formik.handleSubmit} className="text-start mb-4">
                  <CustomTextArea
                    label={`Reason for ${isDisable ? "disabling" : "enabling"}`}
                    name="reason"
                    placeholder={`Enter reason for ${isDisable ? "disabling" : "enabling"} the school`}
                    disabled={loading}
                    showAstreisk={true}
                  />
                </form>
              </FormikProvider>
            )}

            {!requireReason && (
              <p className="text-center mb-4" style={{ fontSize: "15px" }}>
                Are you sure you want to {isDisable ? "disable" : "enable"} this school?
              </p>
            )}

            <div className="d-flex flex-column flex-md-row gap-3 justify-content-center">
              <button
                type="button"
                className="custom-cancel-button"
                onClick={handleModalClose}
                disabled={loading}
              >
                Cancel
              </button>
              <Button
                type="button"
                className="custom-primary-button"
                onClick={formik.handleSubmit}
                disabled={loading}
                style={isDisable ? { backgroundColor: "#dc3545", borderColor: "#dc3545", color: "white" } : {}}
              >
                {loading ? "Processing..." : isDisable ? "Disable School" : "Enable School"}
              </Button>
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ChangeSchoolStatusModal;
